import { useState, useEffect } from 'react';
import { FaGoogle, FaGithub, FaEye, FaEyeSlash, FaFacebook, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './PageCss/AuthForm.css';
import { authService } from '../services/AuthServices';
import { useNavigate } from 'react-router-dom';

const SignupPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userName: '',
  });

  const getInputStatus = (field) => {
    if (!formData[field]) return '';
    switch(field) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? 'valid' : 'invalid';
      case 'password':
        return formData.password.length >= 8 ? 'valid' : 'invalid';
      case 'confirmPassword':
        return formData.password === formData.confirmPassword ? 'valid' : 'invalid';
      case 'userName':
        return formData.userName.length >= 3 ? 'valid' : 'invalid';
      default:
        return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    if (!formData.userName || !formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('All fields are required');
      return;
    }

    if (getInputStatus('email') === 'invalid') {
      toast.error('Please enter a valid email');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await authService.signUp({
        firstName: formData.userName,
        lastName: formData.userName,
        email: formData.email,
        password: formData.password,
        username: formData.userName,
      });

      if (response.success) {
        toast.success('Account created successfully!');
        navigate('/login', { state: { signupSuccess: true } });
      }
      else {
        toast.error(response.errors[0]?.description || response.errors[0] || 'Registration failed');
      }
    } catch (error) {
      toast.error('Failed to create account');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="auth-container">
      <div className="signup-card">
        <div className="auth-header">
          <h1 className="signup-title">Create Account</h1>
          <p className="auth-subtitle">Join our community</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-input-group">
            <div className="input-wrapper">
              <input
                type="text"
                className={`signup-input ${getInputStatus('userName')}`}
                placeholder="Username"
                value={formData.userName}
                onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                required
              />
              {getInputStatus('userName') && (
                <span className="validation-icon">
                  {getInputStatus('userName') === 'valid' ? 
                    <FaCheckCircle className="valid-icon" /> : 
                    <FaTimesCircle className="invalid-icon" />
                  }
                </span>
              )}
            </div>
          </div>

          <div className="auth-input-group">
            <div className="input-wrapper">
              <input
                type="email"
                className={`signup-input ${getInputStatus('email')}`}
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              {getInputStatus('email') && (
                <span className="validation-icon">
                  {getInputStatus('email') === 'valid' ? 
                    <FaCheckCircle className="valid-icon" /> : 
                    <FaTimesCircle className="invalid-icon" />
                  }
                </span>
              )}
            </div>
          </div>

          <div className="auth-input-group">
            <div className="input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className={`signup-input ${getInputStatus('password')}`}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="auth-input-group">
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className={`signup-input ${getInputStatus('confirmPassword')}`}
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="signup-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-divider">
          <span>or continue with</span>
        </div>

        <div className="social-buttons">
          <button className="social-btn">
            <FaGoogle />
          </button>
          <button className="social-btn">
            <FaGithub />
          </button>
          <button className="social-btn">
            <FaFacebook />
          </button>
        </div>

        <div className="auth-footer">
          Already have an account? <a href="/login" className="auth-link">Sign in</a>
        </div>
      </div>

    </div>
    <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default SignupPage;